<template>
  <div class="modal-wrapper">
    <div class="modal-dialog" role="document">
      <div class="modal-content modal-content--scrollable" v-if="selectedRecipient">
        <div class="modal-header">
          <button class="close"
                  @click="$emit('close')"
                  aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
          <h4 class="modal-title">
            Recebedor #{{ selectedRecipient.id }}
          </h4>
        </div>

        <div class="modal-body modal-body--scrollable">
          <div class="modal-body__field">
            <h3>Recebedor</h3>
            <input-field
              label="Nome"
              v-model="recipient.get('name').value"
              :errors="recipient.get('name').errors"
              @input="recipient.get('name').validateField()"
              :disabled="disableIfNotCreate()"
            />
            <select-field
              label="Tipo do documento"
              v-model="recipient.get('document_type').value"
              :errors="recipient.get('document_type').errors"
              :options="recipientEnums.document_types"
              :disabled="disableIfNotCreate()"
              v-if="enumsLoaded"
            />
            <input-field
              label="Documento"
              :mask="documentMask()"
              v-model="recipient.get('document').value"
              @input="recipient.get('document').validateField()"
              :errors="recipient.get('document').errors"
              :disabled="disableIfNotCreate() || !recipient.get('document_type').value"
              v-if="enumsLoaded"
            />
            <hr>
            <h3>Nota fiscal</h3>
            <checkbox-field
              label="Gerar sob demanda?"
              v-model="nfseConfiguration.get('on_demand_generation').value"
              :errors="nfseConfiguration.get('on_demand_generation').errors"
            />
            <div v-if="nfseConfiguration.get('on_demand_generation').value">
              <input-field
                label="Dias de geração"
                placeholder="10,20,30"
                v-model="nfseConfiguration.get('generation_days').value"
                :errors="nfseConfiguration.get('generation_days').errors"
              />
            </div>
            <hr>
            <h3>Conta bancária</h3>
            <select-field
              label="Banco"
              v-model="bankAccount.get('bank_code').value"
              :errors="bankAccount.get('bank_code').errors"
              :options="recipientEnums.bank_codes"
              :disabled="disableIfNeitherCreateNorUpdate()"
              v-if="enumsLoaded"
            />
            <input-field
              label="Agência"
              mask="####"
              v-model="bankAccount.get('agency_code').value"
              :errors="bankAccount.get('agency_code').errors"
              @input="bankAccount.get('agency_code').validateField()"
              :disabled="disableIfNeitherCreateNorUpdate()"
            />
            <input-field
              label="Dígito da agência"
              mask="X"
              v-model="bankAccount.get('agency_digit').value"
              :errors="bankAccount.get('agency_digit').errors"
              @input="bankAccount.get('agency_digit').validateField()"
              :disabled="disableIfNeitherCreateNorUpdate()"
            />
            <div v-if="enumsLoaded">
              <select-field
                label="Tipo da conta"
                v-model="bankAccount.get('bank_account_type').value"
                :errors="bankAccount.get('bank_account_type').errors"
                :options="recipientEnums.bank_account_types"
                :disabled="disableIfNeitherCreateNorUpdate()"
              />
            </div>
            <input-field
              label="Conta"
              mask="#############"
              v-model="bankAccount.get('number').value"
              :errors="bankAccount.get('number').errors"
              @input="bankAccount.get('number').validateField()"
              :disabled="disableIfNeitherCreateNorUpdate()"
            />
            <input-field
              label="Dígito da conta"
              mask="X"
              v-model="bankAccount.get('digit').value"
              :errors="bankAccount.get('digit').errors"
              @input="bankAccount.get('digit').validateField()"
              :disabled="disableIfNeitherCreateNorUpdate()"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-primary" @click="submit()">
            {{ modalActionText }}
          </button>
          <button class="btn btn-default" @click="$emit('close')">
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import RecipientsService from 'services/RecipientsService';
import { FormGroup } from 'utils/form';
import { InputField, SelectField, CheckboxField } from 'components/form';
import _ from 'lodash';

export default {
  components: {
    InputField,
    SelectField,
    CheckboxField,
  },
  computed: {
    ...mapState([
      'selectedRecipient',
      'recipientEnums',
    ]),
    modalActionText() {
      if (this.toCreate) return 'Salvar';
      if (this.toDisable) return 'Desativar';
      if (this.toDisableTransfer) return 'Desabilitar transfêrencias';
      if (this.toEnableTransfer) return 'Habilitar transfêrencias';
      if (this.toUpdate) return 'Atualizar';

      this.$emit('close');
      this.$alert.notifyAlert('Erro inesperado, tente novamente mais tarde');
      return '';
    },
    toCreate() {
      return this.selectedRecipient.modalAction === 'createRecipient';
    },
    toDisable() {
      return this.selectedRecipient.modalAction === 'disableRecipient';
    },
    toDisableTransfer() {
      return this.selectedRecipient.modalAction === 'disableTransfers';
    },
    toEnableTransfer() {
      return this.selectedRecipient.modalAction === 'enableTransfers';
    },
    toUpdate() {
      return this.selectedRecipient.modalAction === 'updateRecipient';
    },
  },
  created() {
    this.$loader.show();

    this.loadRecipientEnums()
      .then(() => {
        this.enumsLoaded = true;
      })
      .catch(() => {
        this.enumsLoaded = false;
        this.$alert.notifyAlert('Erro ao buscar informações, tente novamente mais tarde');
      });

    this.$loader.hide();
  },
  data() {
    return {
      bankAccount: new FormGroup({
        agency_code: { required: true },
        agency_digit: { required: false },
        bank_account_type: { required: true },
        bank_code: { required: true },
        digit: { required: true },
        number: { required: true },
      }),
      enumsLoaded: false,
      recipient: new FormGroup({
        name: { required: true },
        document: { required: true },
        document_type: { required: true },
        institution_id: { required: true },
      }),
      nfseConfiguration: new FormGroup({
        on_demand_generation: {},
        generation_days: {
          requiredIfMatch: { on_demand_generation: { values: ['true', true, 1] } },
          regex: /^\d+(,\s?\d+)*$/,
        },
      }),
      selectDocumentType: [],
    };
  },
  methods: {
    ...mapActions([
      'loadRecipientEnums',
    ]),
    create() {
      if (!this.validForm()) return;

      this.processRequest(
        RecipientsService.create(
          this.recipient.getRawValue(),
          {
            ...this.bankAccount.getRawValue(),
            ...this.nfseConfiguration.getRawValue(),
          },
        ),
        'Recebedor criado com sucesso',
        'Erro ao tentar criar um novo recebedor',
      );
    },
    disable() {
      this.processRequest(
        RecipientsService.disable(this.selectedRecipient.id),
        `Recebedor #${this.selectedRecipient.id} desativado com sucesso`,
        `Erro ao tentar desativar o recebedor #${this.selectedRecipient.id}`,
      );
    },
    disableIfNotCreate() {
      return !this.toCreate;
    },
    disableIfNeitherCreateNorUpdate() {
      return !(this.toCreate || this.toUpdate);
    },
    documentMask() {
      if (this.recipient.get('document_type').value === 'cpf') return '###.###.###-##';

      return '##.###.###/####-##';
    },
    enableTransfers() {
      this.processRequest(
        RecipientsService.updateAllowTransfer(this.selectedRecipient.id, true),
        `As próximas transfêrencias para o recebedor #${this.selectedRecipient.id} foram habilitadas`,
        `Erro ao habilitar próximas transfêrencias do recebedor #${this.selectedRecipient.id}`,
      );
    },
    disableTransfers() {
      this.processRequest(
        RecipientsService.updateAllowTransfer(this.selectedRecipient.id, false),
        `As próximas transfêrencias para o recebedor #${this.selectedRecipient.id} foram desabilitadas`,
        `Erro ao desabilitar próximas transfêrencias do recebedor #${this.selectedRecipient.id}`,
      );
    },
    processRequest(request, successMessage, errorPrefixMessage) {
      this.$loader.show();

      request
        .then(() => {
          this.$emit('close');
          this.$alert.notifySuccess(successMessage);
        })
        .catch(({ response: { data } }) => {
          this.$alert.notifyAlert(`${errorPrefixMessage}: ${data.message}`);
        })
        .finally(() => { this.$loader.hide(); });
    },
    submit() {
      if (this.toCreate) {
        this.create();
      } else if (this.toDisable) {
        this.disable();
      } else if (this.toDisableTransfer) {
        this.disableTransfers();
      } else if (this.toEnableTransfer) {
        this.enableTransfers();
      } else if (this.toUpdate) {
        this.update();
      } else {
        this.$emit('close');
        this.$alert.notifyAlert('Erro inesperado, tente novamente mais tarde');
      }
    },
    update() {
      if (!this.validForm()) return;

      this.processRequest(
        RecipientsService.update(
          this.selectedRecipient.id,
          {
            ...this.bankAccount.getRawValue(),
            ...this.nfseConfiguration.getRawValue(),
          },
        ),
        `Recebedor #${this.selectedRecipient.id} atualizado com sucesso`,
        `Erro ao tentar atualizar o recebedor #${this.selectedRecipient.id}`,
      );
    },
    validForm() {
      const recipientForm = this.recipient.validateForm();
      const bankAccountForm = this.bankAccount.validateForm();
      const nfseConfigForm = this.nfseConfiguration.validateForm();
      return recipientForm && bankAccountForm && nfseConfigForm;
    },
  },
  mounted() {
    this.bankAccount.addValues(_.pick(this.selectedRecipient.bank_account, [
      'agency_code',
      'agency_digit',
      'bank_account_type',
      'bank_code',
      'digit',
      'number',
    ]));

    this.recipient.addValues(_.pick(this.selectedRecipient, [
      'document',
      'document_type',
      'institution_id',
      'name',
    ]));

    this.nfseConfiguration.addValues(_.pick(this.selectedRecipient.nfse_configuration, [
      'on_demand_generation',
      'generation_days',
    ]));
  },
};
</script>

<style type="text/css">
  .input-group-btn>select {
    font-size: 20px;
  }
</style>
